/* =============================================================
   Oadsy Product Page — Vanilla JS
   No external dependencies.

   Classes:
     OadsyGallery        — image gallery (arrows, thumbs, swipe)
     OadsyBundleSelector — metaobject-driven bundle chip picker
     OadsyScentSelector  — scent card grid with selection limit
     OadsyProductPage    — top-level controller, cart logic

   Cart behaviour:
     Starter Kit page  → all selected scents add Navulling variants
                         + 1× free Metalen Clip variant (€0,00)
                         Line item properties brand each navulling
                         with the bundle label ("Starter Kit: 2-Pack")
                         so the customer sees context at cart + checkout.
                         This ensures all items count toward Shopify's
                         automatic discount quantity threshold.
                         See BUNDLE_REDESIGN_PLAN.md for full context.
     Navulling page    → all selected scents add Navulling variants
     Metalen Clip page → single-variant direct add to cart
   ============================================================= */

/* ── Scent note colour map (used to highlight notes in description) ── */
const _NOTE_COLORS = {
  bergamot: "#C17F24",
  lavendel: "#7C6FA0",
  cederhout: "#7A6248",
  sandelhout: "#9B7353",
  iris: "#6B7AB5",
  roos: "#B5667A",
  jasmijn: "#A09040",
  vanille: "#B5893A",
  musk: "#8C8070",
  patchouli: "#5A7A45",
  vetiver: "#607055",
  tonkaboon: "#A06830",
  oud: "#6B4A35",
  amber: "#C89030",
  kruidnagel: "#8C4A25",
  kaneel: "#A05020",
};

function _highlightNotes(text, notes) {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  if (!notes.length) return escaped;
  const sorted = [...notes].sort((a, b) => b.length - a.length);
  const pattern = sorted
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const re = new RegExp(`\\b(${pattern})\\b`, "gi");
  return escaped.replace(re, (match) => {
    const color = _NOTE_COLORS[match.toLowerCase()] || "var(--oadsy-accent)";
    return `<span style="color:${color}">${match}</span>`;
  });
}

/* ================================================================
   OadsyGallery
   Controls image carousel: arrows, thumbnails, touch swipe.
   ================================================================ */
class OadsyGallery {
  constructor(el) {
    this.el = el;
    this.images = [...el.querySelectorAll(".oadsy-gallery__main img")];
    this.thumbs = [...el.querySelectorAll(".oadsy-gallery__thumb")];
    this.counter = el.querySelector(".oadsy-gallery__counter");
    this.active = 0;

    el.querySelector(".oadsy-gallery__arrow--prev")?.addEventListener(
      "click",
      () => this.move(-1),
    );
    el.querySelector(".oadsy-gallery__arrow--next")?.addEventListener(
      "click",
      () => this.move(1),
    );

    this.thumbs.forEach((t, i) =>
      t.addEventListener("click", () => this.goto(i)),
    );

    // Touch swipe (mobile)
    let startX = 0;
    el.querySelector(".oadsy-gallery__main")?.addEventListener(
      "touchstart",
      (e) => {
        startX = e.touches[0].clientX;
      },
      { passive: true },
    );
    el.querySelector(".oadsy-gallery__main")?.addEventListener(
      "touchend",
      (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.move(dx < 0 ? 1 : -1);
      },
      { passive: true },
    );

    this.render();
  }

  goto(i) {
    this.active = i;
    this.render();
  }

  move(dir) {
    this.active = (this.active + dir + this.images.length) % this.images.length;
    this.render();
  }

  render() {
    this.images.forEach((img, i) =>
      img.classList.toggle("is-active", i === this.active),
    );
    this.thumbs.forEach((t, i) =>
      t.classList.toggle("is-active", i === this.active),
    );
    if (this.counter) {
      this.counter.textContent = `${this.active + 1} / ${this.images.length}`;
    }
  }
}

/* ================================================================
   OadsyBundleSelector
   Reads bundle cards rendered by oadsy-bundle-selector.liquid.
   Each card's data-bundle-qty drives the max allowed scent selections.

   onChange({ qty, priceCents, originalCents }) is fired on every selection.
   ================================================================ */
class OadsyBundleSelector {
  constructor(el, onChange) {
    this.el = el;
    this.onChange = onChange;
    this.cards = [...el.querySelectorAll(".oadsy-bundle-card")];
    this.selected = null;

    this.cards.forEach((card) =>
      card.addEventListener("click", () => this._select(card)),
    );

    // Auto-select the first bundle on init
    if (this.cards.length) this._select(this.cards[0]);
  }

  _select(card) {
    this.cards.forEach((c) => {
      c.classList.remove("is-selected");
      c.setAttribute("aria-pressed", "false");
    });
    card.classList.add("is-selected");
    card.setAttribute("aria-pressed", "true");

    this.selected = {
      qty: parseInt(card.dataset.bundleQty || "1", 10),
      label: card.dataset.bundleLabel || "",
      priceCents: parseInt(card.dataset.bundlePrice || "0", 10),
      originalCents: parseInt(card.dataset.bundleOriginal || "0", 10),
    };

    this._updateComposition(card);
    this.onChange(this.selected);
  }

  _updateComposition(card) {
    const panel = document.getElementById("OadsyBundleComposition");
    if (!panel) return;

    const composition = card.dataset.bundleComposition || "";
    const savings = parseInt(card.dataset.bundleSavings || "0", 10);

    const textEl = panel.querySelector(".oadsy-bundle-composition__text");
    const savingsEl = panel.querySelector(".oadsy-bundle-composition__savings");

    if (textEl) {
      if (composition) {
        textEl.textContent = composition;
        textEl.hidden = false;
      } else {
        textEl.hidden = true;
      }
    }

    if (savingsEl) {
      if (savings > 0) {
        savingsEl.textContent = (panel.dataset.savingsLabel || "Bespaar ") + this._fmt(savings);
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }

    panel.hidden = !composition && savings <= 0;
  }

  _fmt(cents) {
    return "\u20AC\u202F" + (cents / 100).toFixed(2).replace(".", ",");
  }
}

/* ================================================================
   OadsyScentSelector
   Manages the scent card grid. Each card tracks a quantity (0–max).
   Multiple of the same variant can be selected (e.g. 2× Oud Tonka).
   Selection order determines which variant becomes the Starter Kit
   line item vs extra Navulling line items.

   Key behaviours:
   - max (set by bundle selection) limits total quantity across all cards
   - setMax(n) trims excess from the end of the selection order
   - orderedItems() returns a flat slot array for cart logic
   ================================================================ */
class OadsyScentSelector {
  constructor(el, { maxSelections, onFocus, onChange, singleSelect }) {
    this.el = el;
    this.max = maxSelections || 1;
    this.onFocus = onFocus;
    this.onChange = onChange;
    this.singleSelect = singleSelect || false;
    this.quantities = new Map(); // card → current quantity
    this.selectionOrder = []; // cards in first-selected order (unique)

    this.cards = [...el.querySelectorAll(".oadsy-scent-card")];
    this._cardEls = new Map(this.cards.map((card) => [
      card,
      {
        qtyValue: card.querySelector(".oadsy-qty__value"),
        btnMinus: card.querySelector(".oadsy-qty__btn--minus"),
        btnPlus:  card.querySelector(".oadsy-qty__btn--plus"),
      },
    ]));

    this.cards.forEach((card) => {
      card.addEventListener("click", (e) => {
        if (!e.target.closest(".oadsy-qty__btn")) {
          if (this.singleSelect) {
            this._add(card);
          } else {
            this._focus(card);
          }
        }
      });

      card
        .querySelector(".oadsy-qty__btn--plus")
        ?.addEventListener("click", (e) => {
          e.stopPropagation();
          this._add(card);
        });
      card
        .querySelector(".oadsy-qty__btn--minus")
        ?.addEventListener("click", (e) => {
          e.stopPropagation();
          this._remove(card);
        });
    });

    if (this.cards.length) this._focus(this.cards[0]);
  }

  /* Update the max allowed total quantity. Called when bundle changes. */
  setMax(max) {
    this.max = max;

    // Trim excess from the end of the selection order
    while (this.total() > max) {
      const last = this.selectionOrder[this.selectionOrder.length - 1];
      const qty = this.quantities.get(last) || 0;
      if (qty > 1) {
        this.quantities.set(last, qty - 1);
      } else {
        this.quantities.delete(last);
        this.selectionOrder.pop();
      }
    }

    this._renderAll();
    this.onChange(this.selectionOrder);
  }

  _focus(card) {
    this.cards.forEach((c) => c.classList.remove("is-focused"));
    card.classList.add("is-focused");
    this.onFocus(card.dataset);
  }

  _add(card) {
    if (card.dataset.available === "false") return;

    if (this.singleSelect) {
      /* Single-select mode: replace any existing selection */
      this.quantities.clear();
      this.selectionOrder = [];
      this.quantities.set(card, 1);
      this.selectionOrder.push(card);
      this._focus(card);
      this._renderAll();
      this.onChange(this.selectionOrder);
      return;
    }

    if (this.total() >= this.max) return;

    const current = this.quantities.get(card) || 0;
    this.quantities.set(card, current + 1);
    if (current === 0) this.selectionOrder.push(card);

    this._focus(card);
    this._renderAll();
    this.onChange(this.selectionOrder);
  }

  _remove(card) {
    const current = this.quantities.get(card) || 0;
    if (current === 0) return;

    if (current === 1) {
      this.quantities.delete(card);
      const idx = this.selectionOrder.indexOf(card);
      if (idx > -1) this.selectionOrder.splice(idx, 1);
    } else {
      this.quantities.set(card, current - 1);
    }

    this._focus(card);
    this._renderAll();
    this.onChange(this.selectionOrder);
  }

  _renderAll() {
    const atMax = this.total() >= this.max;

    this.cards.forEach((card) => {
      const qty = this.quantities.get(card) || 0;
      const isSelected = qty > 0;
      const isSoldOut = card.dataset.available === "false";
      const els = this._cardEls.get(card);

      card.classList.toggle("is-selected", isSelected);
      card.classList.toggle("is-sold-out", isSoldOut);
      els.qtyValue.textContent = qty;
      els.btnMinus.disabled = qty === 0 || isSoldOut;
      els.btnPlus.disabled = atMax || isSoldOut;
    });
  }

  total() {
    let t = 0;
    this.quantities.forEach((q) => {
      t += q;
    });
    return t;
  }

  /*
   * Returns a flat array of cart slots in selection order.
   * Each slot represents one unit to add to the cart.
   *
   * skVariantId        — the product's own variant (SK or Navulling page)
   * navullingVariantId — matched Navulling variant by title (SK pages only)
   *
   * The cart logic in OadsyProductPage decides which ID to use per page type.
   */
  orderedItems() {
    const slots = [];
    for (const card of this.selectionOrder) {
      const qty = this.quantities.get(card) || 0;
      for (let i = 0; i < qty; i++) {
        slots.push({
          skVariantId: parseInt(card.dataset.variantId || "0", 10),
          navullingVariantId: parseInt(
            card.dataset.navullingVariantId || "0",
            10,
          ),
        });
      }
    }
    return slots;
  }
}

/* ================================================================
   OadsyProductPage
   Top-level controller. Wires gallery, bundle selector, scent
   selector, and cart together.
   ================================================================ */
class OadsyProductPage {
  constructor() {
    this.form = document.getElementById("OadsyProductForm");
    if (!this.form) return;

    this.addBtn = document.getElementById("OadsyAddToCart");
    this.addBtnSticky = document.getElementById("OadsyAddToCartSticky");
    this.addBtnBundle = document.getElementById("OadsyAddToCartBundle");
    this.priceEl = document.getElementById("OadsyCurrentPrice");

    this._labels = {
      add:           this.addBtn?.dataset.labelAdd           || "Voeg toe aan winkelwagen",
      soldOut:       this.addBtn?.dataset.labelSoldOut       || "Uitverkocht",
      added:         this.addBtn?.dataset.labelAdded         || "✓ Toegevoegd aan winkelwagen",
      error:         this.addBtn?.dataset.labelError         || "Niet beschikbaar — probeer opnieuw",
      selectOne:     this.addBtn?.dataset.labelSelectOne     || "Selecteer een geur",
      selectMany:    this.addBtn?.dataset.labelSelectMany    || "Selecteer {n} geuren",
      remainingOne:  this.addBtn?.dataset.labelRemainingOne  || "Nog 1 geur te kiezen",
      remainingMany: this.addBtn?.dataset.labelRemainingMany || "Nog {n} geuren te kiezen",
    };
    this.compareEl = document.getElementById("OadsyComparePrice");
    this.discountEl = document.getElementById("OadsyDiscountBadge");

    this.isStarterKit = this.form.dataset.isStarterKit === "true";
    this.bundle = null;

    this._initGallery();
    this._initBundle();
    this._initScent();
    this._initCart();
    this._initStickyVisibility();
  }

  /* ── Sticky CTA visibility ────────────────────────────────── */
  _initStickyVisibility() {
    if (!this.addBtnSticky) return;
    const sticky = this.addBtnSticky.closest(".oadsy-sticky-cta");
    if (!sticky) return;

    // The in-flow ATC button shown on mobile is the bundle button
    // (variant products). Simple products have no in-flow ATC on
    // mobile, so the sticky stays permanently visible there.
    const target = this.addBtnBundle;
    if (!target) {
      sticky.classList.add("is-visible");
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle("is-visible", !entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(target);
  }

  /* ── Gallery ──────────────────────────────────────────────── */
  _initGallery() {
    const el = document.getElementById("OadsyGallery");
    if (el) this.gallery = new OadsyGallery(el);
  }

  /* ── Bundle selector ──────────────────────────────────────── */
  _initBundle() {
    const el = document.getElementById("OadsyBundleSelector");
    if (!el) return;

    this.bundleSelector = new OadsyBundleSelector(el, (bundle) => {
      this.bundle = bundle;
      this._updatePrice();
      this.scentSelector?.setMax(bundle.qty);
      this._updateButton();
    });
  }

  /* ── Scent selector / simple product ─────────────────────── */
  _initScent() {
    const el = document.getElementById("OadsyScentSelector");

    if (!el) {
      // No scent selector = simple product (Metalen Clip)
      this._enableSimpleProduct();
      return;
    }

    const initialMax = this.bundle?.qty || 1;

    this.scentSelector = new OadsyScentSelector(el, {
      maxSelections: initialMax,
      onFocus: (data) => this._onScentFocus(data),
      onChange: () => this._updateButton(),
    });

    this._updateButton();
  }

  /* Metalen Clip: no scent selector — enable button directly. */
  _enableSimpleProduct() {
    const input = document.getElementById("OadsySimpleVariantId");
    const variantId = input?.value;
    if (!variantId) return;

    const isAvailable = input.dataset.available !== "false";

    [this.addBtn, this.addBtnSticky, this.addBtnBundle].forEach((btn) => {
      if (!btn) return;
      if (isAvailable) {
        btn.disabled = false;
        btn.textContent = this._labels.add;
        btn.addEventListener("click", () =>
          this._cartAdd([{ id: parseInt(variantId, 10), quantity: 1 }]),
        );
      } else {
        btn.disabled = true;
        btn.textContent = this._labels.soldOut;
      }
    });
  }

  /* ── Scent focus → update description panel ───────────────── */
  _onScentFocus(data) {
    let notes = [];
    try {
      notes = JSON.parse(data.scentNotes || "[]");
    } catch (_) {}

    // Scent name heading in the info panel
    const nameEl = document.getElementById("OadsyScentName");
    if (nameEl) nameEl.textContent = data.scentName || "";

    // Geuromschrijving — fade transition after first render
    const descEl = document.getElementById("OadsyScentDesc");
    if (descEl) {
      if (descEl.dataset.ready) {
        descEl.style.opacity = "0";
        setTimeout(() => {
          descEl.innerHTML = _highlightNotes(
            data.scentDescription || "",
            notes,
          );
          descEl.style.opacity = "1";
        }, 140);
      } else {
        descEl.innerHTML = _highlightNotes(data.scentDescription || "", notes);
        descEl.dataset.ready = "1";
      }
    }

    // Scent note tags
    const tagsEl = document.getElementById("OadsyScentTags");
    if (tagsEl) {
      tagsEl.innerHTML = "";
      notes.forEach((note) => {
        const span = document.createElement("span");
        span.className = "oadsy-scent-tag";
        span.textContent = note;
        tagsEl.appendChild(span);
      });
    }

    // Strength bar segments
    const strength = parseInt(data.scentStrength || "0", 10);
    document
      .querySelectorAll(".oadsy-strength-segment")
      .forEach((seg, i) => seg.classList.toggle("is-filled", i < strength));

    // Strength label
    const labelEl = document.getElementById("OadsyStrengthLabel");
    if (labelEl) {
      const labels = ["", "Subtiel", "Licht", "Gematigd", "Krachtig", "Intens"];
      labelEl.textContent = labels[strength] || "";
    }
  }

  /* ── Price display ────────────────────────────────────────── */
  _updatePrice() {
    if (!this.bundle || !this.priceEl) return;
    const { priceCents, originalCents } = this.bundle;

    if (priceCents > 0) {
      this.priceEl.textContent = this._fmt(priceCents);
    }

    if (this.compareEl) {
      const show = originalCents > 0 && originalCents > priceCents;
      this.compareEl.textContent = show ? this._fmt(originalCents) : "";
      this.compareEl.hidden = !show;
    }

    if (this.discountEl) {
      const pct =
        originalCents > 0
          ? Math.round((1 - priceCents / originalCents) * 100)
          : 0;
      this.discountEl.textContent = pct > 0 ? `\u2212${pct}%` : "";
      this.discountEl.hidden = pct === 0;
    }
  }

  /* ── Button state ─────────────────────────────────────────── */
  _updateButton() {
    const total = this.scentSelector?.total() ?? 0;
    const max = this.bundle?.qty ?? 1;

    [this.addBtn, this.addBtnSticky, this.addBtnBundle].forEach((btn) => {
      if (!btn) return;

      if (total === 0) {
        btn.disabled = true;
        btn.textContent =
          max === 1 ? this._labels.selectOne : this._labels.selectMany.replace("{n}", max);
      } else if (total < max) {
        btn.disabled = true;
        const remaining = max - total;
        btn.textContent = remaining === 1
          ? this._labels.remainingOne
          : this._labels.remainingMany.replace("{n}", remaining);
      } else {
        btn.disabled = false;
        btn.textContent = this._labels.add;
      }
    });
  }

  /* ── Cart init ────────────────────────────────────────────── */
  _initCart() {
    // Simple product buttons are wired in _enableSimpleProduct.
    // Variant product buttons are wired here.
    if (this.scentSelector) {
      [this.addBtn, this.addBtnSticky, this.addBtnBundle].forEach((btn) =>
        btn?.addEventListener("click", () => this._addVariantToCart()),
      );
    }
  }

  /* ── Cart — variant products ──────────────────────────────── */
  async _addVariantToCart() {
    if (!this.scentSelector) return;
    const orderedItems = this.scentSelector.orderedItems();
    if (!orderedItems.length) return;

    let items;

    if (this.isStarterKit) {
      /*
       * Starter Kit cart logic:
       *   Slot 0  → 1× SK variant (skVariantId) — bundle app expands to navulling + clip.
       *   Slots 1+ → 1× Navulling variant each (navullingVariantId, matched by scent title).
       *
       * Result: always exactly 1 clip regardless of bundle size.
       * Navulling automatic discount rules fire on the extra Navulling variants.
       */
      items = orderedItems
        .map((slot, i) => ({
          id: i === 0 ? slot.skVariantId : slot.navullingVariantId,
          quantity: 1,
        }))
        .filter((item) => item.id > 0);
    } else {
      /*
       * Navulling cart logic:
       *   All slots → 1× Navulling variant each (skVariantId on Navulling page).
       * Consolidate duplicate variant IDs into a single line item with quantity > 1.
       */
      const rawItems = orderedItems.map(({ skVariantId }) => ({
        id: skVariantId,
        quantity: 1,
      }));

      const itemMap = new Map();
      for (const item of rawItems) {
        itemMap.set(item.id, (itemMap.get(item.id) || 0) + item.quantity);
      }
      items = [...itemMap.entries()].map(([id, quantity]) => ({
        id,
        quantity,
      }));
    }

    await this._cartAdd(items);
  }

  /* ── Cart — shared fetch ──────────────────────────────────── */
  async _cartAdd(items) {
    const btns = [this.addBtn, this.addBtnSticky, this.addBtnBundle].filter(Boolean);
    btns.forEach((b) => {
      b.disabled = true;
      b.textContent = "\u2026";
    });

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items,
          sections: ["cart-drawer", "cart-icon-bubble"],
          sections_url: window.location.pathname,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.description || "Cart error");
      }

      const state = await res.json();

      btns.forEach((b) => {
        b.textContent = this._labels.added;
      });

      const cartDrawerEl = document.querySelector("cart-drawer");
      cartDrawerEl?.classList.remove("is-empty");
      cartDrawerEl?.renderContents?.(state);

      // Restore button after 2.5 s
      setTimeout(() => {
        if (this.scentSelector) {
          this._updateButton();
        } else {
          btns.forEach((b) => {
            b.disabled = false;
            b.textContent = this._labels.add;
          });
        }
      }, 2500);
    } catch (err) {
      console.error("[Oadsy] Cart error:", err);
      btns.forEach((b) => {
        b.disabled = true;
        b.textContent = this._labels.error;
      });
      setTimeout(() => {
        if (this.scentSelector) {
          this._updateButton();
        } else {
          btns.forEach((b) => {
            b.disabled = false;
            b.textContent = this._labels.add;
          });
        }
      }, 3000);
    }
  }

  _fmt(cents) {
    return (cents / 100).toFixed(2).replace(".", ",");
  }
}

/* ── Boot ───────────────────────────────────────────────────── */
(function () {
  const boot = () => new OadsyProductPage();
  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", boot)
    : boot();
})();

/* ── Scent shelf quick-add (standalone — independent of OadsyProductPage) ── */
(function () {
  function quickAdd(btn, variantId) {
    btn.disabled = true;
    btn.textContent = "\u2026";

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: ["cart-drawer", "cart-icon-bubble"],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw e; });
        return res.json();
      })
      .then(function (state) {
        btn.textContent = "\u2713";

        var drawerEl = document.querySelector("cart-drawer");
        if (drawerEl) drawerEl.classList.remove("is-empty");
        if (drawerEl && typeof drawerEl.renderContents === "function") drawerEl.renderContents(state);

        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = "+";
        }, 2500);
      })
      .catch(function (err) {
        console.error("[Oadsy] Quick-add error:", err);
        btn.disabled = false;
        btn.textContent = "+";
      });
  }

  function attachQuickAdd() {
    document.querySelectorAll("[data-quick-add]").forEach(function (btn) {
      var variantId = parseInt(btn.dataset.quickAdd, 10);
      if (!variantId) return;
      btn.addEventListener("click", function () { quickAdd(btn, variantId); });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachQuickAdd);
  } else {
    attachQuickAdd();
  }
})();
